import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { useAppContext } from '../../context/AppContext.js';

import {
  REPORT_THEME_DARK,
  REPORT_THEME_LIGHT,
  REPORT_THEME_READY,
  themedReportHtml,
} from './reportTheme.js';

export function useReportTheme(html: string | null) {
  const { colorScheme } = useAppContext();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const themePort = useRef<MessagePort | null>(null);
  const currentScheme = useRef(colorScheme);
  currentScheme.current = colorScheme;
  // Capture the palette only for a new document. Replacing srcDoc on a theme
  // switch would discard expanded rows, filters, and the scroll position.
  const srcDoc = useMemo(
    () =>
      html === null ? undefined : themedReportHtml(html, currentScheme.current),
    [html],
  );
  const syncTheme = useCallback(() => {
    themePort.current?.postMessage(
      currentScheme.current === 'dark' ? REPORT_THEME_DARK : REPORT_THEME_LIGHT,
    );
  }, []);
  useLayoutEffect(() => {
    if (html === null) return;
    const receive = (event: MessageEvent<unknown>) => {
      const reportWindow = frameRef.current?.contentWindow;
      if (
        !reportWindow ||
        event.source !== reportWindow ||
        event.data !== REPORT_THEME_READY
      ) {
        return;
      }
      const port = event.ports[0];
      if (!port || event.ports.length !== 1) {
        for (const extraPort of event.ports) extraPort.close();
        return;
      }
      themePort.current?.close();
      themePort.current = port;
      syncTheme();
    };
    window.addEventListener('message', receive);
    return () => {
      window.removeEventListener('message', receive);
      themePort.current?.close();
      themePort.current = null;
    };
  }, [html, syncTheme]);
  useLayoutEffect(syncTheme, [colorScheme, syncTheme]);

  // Channel setup also sends the latest theme if loading delayed the handshake.
  return { frameRef, srcDoc, syncTheme };
}
